import { Constraint, StationeryProduct, StationeryShippingOption, StationeryShippingOptionV1 } from '@shared/utils/print-price/types';
import { calculateStationeryPricing } from '@shared/utils/print-price/utils/calculatePrices';

const handlePOBoxConstraint = (constraint: Constraint & { __typename: 'StationeryAllowPoBoxShippingConstraint' }, options: ShippingMethodFilters): boolean => {
  if (!constraint.allowPoBox && options.isPOBox) {
    return false;
  }

  return true;
};

const handleCountryShippingConstraint = (constraint: Constraint & { __typename: 'StationeryCountryShippingConstraint' }, options: ShippingMethodFilters): boolean => {
  const { includedCountries, excludedCountries } = constraint;
  const currentCountry = options.countryCode;

  return (includedCountries?.some(country => currentCountry === country) ?? true) && (excludedCountries?.every(country => currentCountry !== country) ?? true);
};

const handleQuantityShippingConstraint = (constraint: Constraint & { __typename: 'StationeryQuantityShippingConstraint' }, options: ShippingMethodFilters) => {
  const { minQuantity, maxQuantity } = constraint;
  const quantity = options.quantity;

  return quantity >= minQuantity && quantity <= (maxQuantity ?? quantity);
};

const handleConstraint = (constraint: Constraint, options: ShippingMethodFilters): boolean => {
  switch (constraint.__typename) {
    case 'StationeryAllowPoBoxShippingConstraint': {
      return handlePOBoxConstraint({ ...constraint, __typename: 'StationeryAllowPoBoxShippingConstraint' }, options);
    }
    case 'StationeryCountryShippingConstraint': {
      return handleCountryShippingConstraint({ ...constraint, __typename: 'StationeryCountryShippingConstraint' }, options);
    }
    case 'StationeryQuantityShippingConstraint': {
      return handleQuantityShippingConstraint({ ...constraint, __typename: 'StationeryQuantityShippingConstraint' }, options);
    }
    default: {
      return false;
    }
  }
};

// Handle a StationeryShippingOptionV1
// Returns null if constraints are not met.
const processShippingOptionV1 = (input: StationeryShippingOptionV1, options: ShippingMethodFilters): ShippingMethodInfo | null => {
  const price = calculateStationeryPricing(input.pricing, options.quantity);
  const isValid = input.constraints.every(c => handleConstraint(c, options));

  if (!isValid) {
    return null;
  }

  return {
    id: input.id,
    title: input.label,
    price: price,
    description: input.description ?? undefined,
  };
};

// Convert any StationeryShippingOption into ShippingMethodInfo
// following proper process for ShippingOption's version
// Returns null if constraints are not met.
const getShippingInfo = (input: StationeryShippingOption, options: ShippingMethodFilters): ShippingMethodInfo | null => {
  switch (input.__typename) {
    case 'StationeryProductShippingOptionV1': {
      return processShippingOptionV1(input, options);
    }
  }

  return null;
};

const isNotNull = (input: unknown): input is ShippingMethodInfo => {
  return !!input;
};

export const useGetValidProductShippingOptions = (product: StationeryProduct, options: ShippingMethodFilters): ShippingMethodInfo[] => {
  const shippingMethods: StationeryShippingOption[] | null | undefined = product.shippingOptions;

  if (!shippingMethods) {
    return [];
  }

  const processedShippingMethods = shippingMethods.map(method => getShippingInfo(method, options)).filter(isNotNull);

  return processedShippingMethods.sort((a, b) => {
    return a.price - b.price;
  });
};

type ShippingMethodFilters = {
  quantity: number;
  isPOBox: boolean;
  countryCode: string;
};

type ShippingMethodInfo = {
  id: string;
  title: string;
  description?: string;
  price: number;
};
